/**
* In this file we handle to show the SelectedPrescriptionTemplate details popup screen
**/
import { Modal, Button, Carousel } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function SelectedPrescriptionTemplate(props) {
    console.log('SelectedPrescriptionTemplate props:', props);
    // Initial state declaration - default first template selected
    const [selectedTemplate, setSelectedTemplate] = useState('prescriptionTemplate1');
    const [activeIndex, setActiveIndex] = useState(0);
    const [htmlContents, setHtmlContents] = useState({});
    const role = Number(localStorage.getItem('role'));
    const { t } = useTranslation();
    const physicianId = props.physicianData.account;
    // Template data
    const templates = [
        {
            id: 'prescriptionTemplate1',
            name: `${t('modal.template1')}`,
            htmlPath: '/templates/prescription/prescriptionTemplate1.html' // <-- public path
        },
        {
            id: 'prescriptionTemplate2',
            name: `${t('modal.template2')}`,
            htmlPath: '/templates/prescription/prescriptionTemplate2.html'
        },
        {
            id: 'prescriptionTemplate3',
            name: `${t('modal.template3')}`,
            htmlPath: '/templates/prescription/prescriptionTemplate3.html'
        },
        ...(physicianId === '1c7d1bb8-88cc-4428-896e-899b01865de8' // --> The 4th template should only be visible to Dr. Vindhiya K and to Hospital Admin physicians from Nellia Child Care
        ? [
            {
            id: 'prescriptionTemplate4',
            name: `${t('modal.template4')}`,
            htmlPath: '/templates/prescription/prescriptionTemplate4.html',
            },
        ]
        : [])
    ];

    // Load HTML template and populate with physician data
    const loadHTMLTemplate = async (templateId) => {
        const template = templates.find(t => t.id === templateId);
        if (!template) return;

        try {
            console.log(`[LOAD] Fetching template: ${template.htmlPath}`);
            const response = await fetch(template.htmlPath);
            if (!response.ok) {
                console.warn(`[ERROR] Failed to fetch template (${response.status}): ${template.htmlPath}`);
                return '<p style="color:red">Error loading template</p>';
            }

            const organizationData = props.organizationData || [];
            let consultationTime = '--';

            if (organizationData.length) {
                // Group schedules by identical start-end times and compress consecutive days into ranges
                const formatTime = (timeStr) => {
                    const [h, m] = timeStr.split(':');
                    const date = new Date();
                    date.setHours(h, m);
                    return date.toLocaleTimeString('en-IN', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                    });
                };

                const dayOrder = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
                const shortDayMap = {
                    Monday: "Mon",
                    Tuesday: "Tue",
                    Wednesday: "Wed",
                    Thursday: "Thu",
                    Friday: "Fri",
                    Saturday: "Sat",
                    Sunday: "Sun",
                };

                const dayTimeMap = {};

                for (const schedule of organizationData) {
                    const formattedStart = formatTime(schedule.startTime);
                    const formattedEnd = formatTime(schedule.endTime);
                    const timeKey = `${formattedStart} - ${formattedEnd}`;

                    for (const day of schedule.days) {
                        if (!dayTimeMap[timeKey]) dayTimeMap[timeKey] = [];
                        dayTimeMap[timeKey].push(day);
                    }
                }

                const groupDays = (days) => {
                    // sort according to week order
                    days.sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));

                    const result = [];
                    let rangeStart = days[0];
                    let prev = days[0];

                    for (let i = 1; i <= days.length; i++) {
                        const current = days[i];
                        const prevIndex = dayOrder.indexOf(prev);
                        const currIndex = dayOrder.indexOf(current);

                        if (i < days.length && currIndex === prevIndex + 1) {
                            // still continuous → extend range
                            prev = current;
                            continue;
                        } else {
                            // range ends
                            if (rangeStart === prev) {
                                result.push(shortDayMap[rangeStart]);
                            } else {
                                result.push(`${shortDayMap[rangeStart]} - ${shortDayMap[prev]}`);
                            }
                            rangeStart = current;
                            prev = current;
                        }
                    }
                    return result.join(', ');
                };

                let parts = [];
                for (const [time, days] of Object.entries(dayTimeMap)) {
                    const formattedDays = groupDays(Array.from(new Set(days)));
                    parts.push(`Consultation Time: ${formattedDays} ${time}`);
                }

                consultationTime = parts.join('\n');
            }

            let htmlTemplate = await response.text();
            console.log(`[LOAD] Template content length: ${htmlTemplate.length}`);

            console.log(`[TEMPLATE CONTENT]\n${htmlTemplate}`);

            // Replace placeholders
            const physicianData = props.physicianData || {};

            // --- hospital data extraction (supports a few possible shapes) ---
            const org = props.organizationData || {};
            let hospitalName = '';
            let hospitalPhoto = null; // could be data URL, absolute url or filename

            // Support organizationData being an array or object and a few common field names
            if (Array.isArray(org) && org.length > 0) {
                const first = org[0] || {};
                hospitalName = first.name || first.hospital_name || first.hospitalName || '';
                hospitalPhoto = first.photo || first.hospital_photo || first.hospitalPhoto || null;
            } else if (typeof org === 'object' && org !== null) {
                hospitalName = org.name || org.hospital_name || org.hospitalName || (org.hospital && (org.hospital.name || '')) || '';
                hospitalPhoto = org.photo || org.hospital_photo || org.hospitalPhoto || (org.hospital && org.hospital.photo) || null;
            }

            hospitalName = hospitalName || '--';

            // Build an image source for the hospital photo. If it's already a data URL or absolute URL, use it.
            // Otherwise, assume it's a filename served from a public/uploads or /images path. This is a best-effort
            // fallback — the backend (PrescriptionController) embeds base64 data URLs when rendering server-side.
            let hospitalPhotoSrc = '';
            if (hospitalPhoto) {
                if (typeof hospitalPhoto === 'string' && hospitalPhoto.startsWith('data:')) {
                    hospitalPhotoSrc = hospitalPhoto;
                } else if (typeof hospitalPhoto === 'string' && /^(https?:)?\//.test(hospitalPhoto)) {
                    // absolute or root-relative URL
                    hospitalPhotoSrc = hospitalPhoto;
                } else if (typeof hospitalPhoto === 'string') {
                    // fallback path; adjust if your app serves uploads from a different path
                    hospitalPhotoSrc = `${process.env.REACT_APP_SERVERURL}/${hospitalPhoto}`;
                }
            }

            // Compute simple initials for the small logo fallback (e.g. 'HN')
            const hospitalInitials = (hospitalName && hospitalName !== '--')
                ? hospitalName.split(' ').filter(Boolean).slice(0,2).map(s => s[0]).join('').toUpperCase()
                : 'HN';

            htmlTemplate = htmlTemplate
                .replace(/{{physicianName}}/g, `${physicianData.first_name || ''} ${physicianData.last_name || ''}`.trim() || '--')
                .replace(/{{specialization}}/g, physicianData.specialist || '--')
                .replace(/{{registerNumber}}/g, physicianData.register_number || '--')
                .replace(/{{consultationTime}}/g, consultationTime)
                .replace(/{{currentDate}}/g, new Date().toLocaleDateString())

                // Hospital replacements: support several placeholder styles used across templates
                .replace(/{{hospitalName}}/g, hospitalName)
                .replace(/{{hospitalPhoto}}/g, hospitalPhotoSrc)
                .replace(/<%= hospital\.name %>/g, hospitalName)
                .replace(/Hospital Name/g, hospitalName)
                // Replace the short logo text (HN) with computed initials — keeps layout similar
                .replace(/>HN</g, `>${hospitalInitials}<`) // matches the inner text in tags like <div class="hospital-logo">HN</div>
                // Handle template4 EJS no-logo expression: replace with appropriate class value
                .replace(/<%= !hospitalPhoto \? 'no-logo' : '' %>/g, hospitalPhotoSrc ? '' : 'no-logo')
                // Replace any remaining simple occurrences of hospital photo EJS token
                .replace(/<%= hospitalPhoto %>/g, hospitalPhotoSrc)
                ;

            return htmlTemplate;
        } catch (err) {
            console.error(`[EXCEPTION] While loading template: ${err}`);
            return '<p style="color:red">Exception while loading template</p>';
        }
    };

    // Handle template selection
    const handleTemplateSelect = async (templateId) => {
        setSelectedTemplate(templateId);

        // If already loaded, skip reloading
        if (!htmlContents[templateId]) {
            const html = await loadHTMLTemplate(templateId);
            setHtmlContents(prev => ({ ...prev, [templateId]: html }));
        }
    };

    // Handle carousel slide change
    const handleSelect = async (selectedIndex) => {
        setActiveIndex(selectedIndex);
        const templateId = templates[selectedIndex].id;
        setSelectedTemplate(templateId);

        if (!htmlContents[templateId]) {
            const html = await loadHTMLTemplate(templateId);
            setHtmlContents(prev => ({ ...prev, [templateId]: html }));
        }
    };

    // Load initial template
    useEffect(() => {
        const init = async () => {
            console.log('[INIT] Loading initial template...');
            const html = await loadHTMLTemplate(selectedTemplate);
            setHtmlContents({ [selectedTemplate]: html });
        };
        init();
    }, [props.physicianData, props.organizationData]);

    // Handle save/confirm selection
    const handleSave = () => {
        if (selectedTemplate) {
            // Store the selected template
            localStorage.setItem('selectedPrescriptionTemplate', selectedTemplate);

            // If parent component needs the selected template, pass it via props callback
            if (props.onTemplateSelect) {
                props.onTemplateSelect(selectedTemplate);
            }

            props.handleClose();
        }
    };

    return (
        <Modal
            show={props.data.showPrescriptionTemplate}
            onHide={props.handleClose}
            backdrop="static"
            keyboard={false}
            size="xl"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            <Modal.Header closeButton className={`${role === 4 ? 'admin-modal-header' : ''}`}>
                <Modal.Title>
                    <h5 className="modal-title">
                        <span className="text-capitalize">{t('modal.selectPrescriptionTemplate')}</span>
                    </h5>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ padding: '5px', height: 'calc(150vh - 200px)', overflow: 'hidden' }}>
                <style jsx>{`
                    .modal-content
                    {
                        width: 80%;
                    }
                    .modal-dialog.modal-xl.modal-dialog-centered {
                        justify-content: center;
                    }
                    .prescription-template-carousel {
                        position: relative;
                        margin: 0;
                        padding: 0;
                        height: 100%;
                    }
                    .prescription-template-carousel .carousel-inner {
                        height: 100%;
                    }
                    .prescription-template-carousel .carousel-control-prev,
                    .prescription-template-carousel .carousel-control-next {
                        background-color: rgba(0, 0, 0, 0.3);
                        border-radius: 50%;
                        width: 35px;
                        height: 35px;
                        top: 50%;
                        transform: translateY(-50%);
                        z-index: 10;
                    }
                    .prescription-template-carousel .carousel-control-prev {
                        left: 5px;
                    }
                    .prescription-template-carousel .carousel-control-next {
                        right: 5px;
                    }
                    .prescription-template-carousel .carousel-control-prev-icon,
                    .prescription-template-carousel .carousel-control-next-icon {
                        background-color: ${role === 4 ? '#00b894' : '#007bff'};
                        border-radius: 50%;
                        padding: 4px;
                        width: 16px;
                        height: 16px;
                    }
                    .template-card {
                        transition: all 0.3s ease;
                        margin: 0;
                        height: 100%;
                        display: flex;
                        flex-direction: column;
                    }
                    .template-card:hover {
                        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                        transform: translateY(-2px);
                    }
                    .carousel-indicators {
                        bottom: 5px;
                        margin-bottom: 0;
                    }
                    .carousel-indicators [data-bs-target] {
                        background-color: ${role === 4 ? '#00b894' : '#007bff'};
                    }
                    .html-preview {
                        border: 1px solid #dee2e6;
                        border-radius: 6px;
                        overflow: hidden;
                        background: white;
                        flex: 1;
                        width: 100%;
                        position: relative;
                    }
                    .html-preview iframe {
                        width: 100%;
                        height: 100%;
                        border: none;
                        transform: scale(0.4);
                        transform-origin: top left;
                        position: absolute;
                        top: 0;
                        left: 0;
                    }
                    .carousel-item {
                        padding: 3px;
                        height: 100%;
                    }
                    .carousel-item-content {
                        height: 100%;
                        display: flex;
                        justify-content: center;
                        align-items: stretch;
                    }
                    .selection-indicator {
                        margin-top: 8px;
                        margin-bottom: 0;
                        flex-shrink: 0;
                    }
                    .template-name {
                        margin-top: 8px;
                        margin-bottom: 4px;
                        flex-shrink: 0;
                    }
                `}</style>

                {/* Carousel for Templates */}
                <div className="position-relative h-100">
                    <Carousel
                        activeIndex={activeIndex}
                        onSelect={handleSelect}
                        indicators={true}
                        controls={true}
                        interval={null} // Disable auto-slide
                        className="prescription-template-carousel h-100"
                    >
                        {templates.map((template, index) => (
                            <Carousel.Item key={template.id} className="h-100">
                                <div className="carousel-item-content">
                                    <div
                                        className={`template-card ${selectedTemplate === template.id ? 'selected' : ''}`}
                                        onClick={() => handleTemplateSelect(template.id)}
                                        style={{
                                            border: selectedTemplate === template.id
                                                ? `2px solid ${role === 4 ? '#00b894' : '#007bff'}`
                                                : '1px solid #dee2e6',
                                            borderRadius: '6px',
                                            padding: '8px',
                                            cursor: 'pointer',
                                            backgroundColor: selectedTemplate === template.id ? '#f8f9fa' : '#fff',
                                            width: '95%',
                                            maxWidth: '800px'
                                        }}
                                    >
                                        {/* HTML Template Preview */}
                                        <div className="html-preview">
                                            <iframe
                                                src={
                                                    htmlContents[template.id]
                                                        ? URL.createObjectURL(new Blob([htmlContents[template.id]], { type: 'text/html' }))
                                                        : undefined
                                                }
                                                title={template.name}
                                                style={{
                                                    width: '250%',
                                                    height: '250%'
                                                }}
                                            />
                                        </div>

                                        {/* Template Name and Selection */}
                                        <div className="text-center">
                                            <h6 className={`template-name ${role === 4 ? 'text-tmAquaDgreen' : 'text-tmBlue'}`}>
                                                {template.name}
                                                {selectedTemplate === template.id && (
                                                    <i className={`fa-solid fa-check-circle ms-2 ${role === 4 ? 'text-tmAquaDgreen' : 'text-tmBlue'}`}></i>
                                                )}
                                            </h6>
                                        </div>
                                    </div>
                                </div>
                            </Carousel.Item>
                        ))}
                    </Carousel>
                </div>
            </Modal.Body>

            {/* Footer */}
            <Modal.Footer style={{ padding: '10px 20px' }}>
                <Button
                    variant={`btn btn-sm ${role === 4 ? 'btn-tmAquaDgreen' : 'btn-tmBlue'}`}
                    onClick={handleSave}
                    disabled={!selectedTemplate}
                >
                    {t('modal.confirmSelection')}
                </Button>
                <Button
                    variant="secondary"
                    className={`btn btn-sm ${role === 4 ? 'btn-tmAquaGreen' : 'btn-tmBlue'}`}
                    onClick={props.handleClose}
                >
                    {t('modal.close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default SelectedPrescriptionTemplate;